/**
 * Clipboard Adapter
 *
 * Cross-platform clipboard abstraction.
 * Web uses navigator.clipboard, React Native uses @react-native-clipboard/clipboard.
 *
 * @example
 * // React Native initialization
 * import Clipboard from '@react-native-clipboard/clipboard';
 * import { setClipboardAdapter } from '@prosperia/shared';
 *
 * setClipboardAdapter({
 *   copy: (text) => Clipboard.setString(text),
 *   read: () => Clipboard.getString(),
 * });
 */

let clipboardAdapter = null

/**
 * Set the clipboard adapter for the current platform
 * @param {object} adapter - Adapter with copy and read methods
 * @param {function} adapter.copy - Function to copy text (text) => Promise<void>|void
 * @param {function} adapter.read - Function to read clipboard () => Promise<string>|string
 */
export const setClipboardAdapter = (adapter) => {
  clipboardAdapter = adapter
}

/**
 * Get the current clipboard adapter
 * @returns {object|null} The clipboard adapter
 */
export const getClipboardAdapter = () => clipboardAdapter

/**
 * Copy text to clipboard
 * Uses the platform adapter if set, falls back to navigator.clipboard on web
 * @param {string} text - Text to copy
 * @returns {Promise<boolean>} True if successful
 */
export const copyToClipboard = async (text) => {
  try {
    // Use adapter if available
    if (clipboardAdapter?.copy) {
      await clipboardAdapter.copy(text)
      return true
    }

    // Web fallback - navigator.clipboard
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      await navigator.clipboard.writeText(text)
      return true
    }

    // Legacy fallback for older browsers
    if (typeof document !== 'undefined') {
      const textArea = document.createElement('textarea')
      textArea.value = text
      textArea.style.position = 'fixed'
      textArea.style.left = '-9999px'
      textArea.style.top = '-9999px'
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()

      try {
        document.execCommand('copy')
        return true
      } finally {
        document.body.removeChild(textArea)
      }
    }

    console.warn('[clipboard] No clipboard API available')
    return false
  } catch (error) {
    console.error('[clipboard] Error copying to clipboard:', error)
    return false
  }
}

/**
 * Read text from clipboard
 * Uses the platform adapter if set, falls back to navigator.clipboard on web
 * @returns {Promise<string|null>} Clipboard text or null if unavailable
 */
export const readFromClipboard = async () => {
  try {
    // Use adapter if available
    if (clipboardAdapter?.read) {
      const text = await clipboardAdapter.read()
      return text
    }

    // Web fallback - navigator.clipboard
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      const text = await navigator.clipboard.readText()
      return text
    }

    console.warn('[clipboard] No clipboard read API available')
    return null
  } catch (error) {
    console.error('[clipboard] Error reading from clipboard:', error)
    return null
  }
}

/**
 * Check if clipboard is available on this platform
 * @returns {boolean} True if clipboard operations are supported
 */
export const isClipboardAvailable = () => {
  if (clipboardAdapter?.copy) return true
  if (typeof navigator !== 'undefined' && navigator.clipboard) return true
  if (typeof document !== 'undefined') return true // Legacy fallback
  return false
}
